/**
 * PINNED SHARED CONTRACT — MOPA Pixelator Web
 *
 * Owned by the orchestrator. Build agents code against these types and MUST NOT
 * edit this file; interface change requests go through the orchestrator.
 *
 * Conventions:
 * - All geometry is in millimeters, project coordinate space (Y down, matching
 *   LightBurn XML and SVG). 1 SVG user unit = 1 mm.
 * - Rings are flat arrays [x0,y0,x1,y1,...] and are implicitly closed (last
 *   point connects to first; do NOT duplicate the first point at the end).
 * - Regions use even-odd fill over their rings.
 * - Colors are uppercase "#RRGGBB".
 */

// ---------- primitives ----------

/** 2D affine matrix [a, b, c, d, e, f]: (x,y) -> (a·x + c·y + e, b·x + d·y + f). */
export type Mat = [number, number, number, number, number, number];

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

export interface Pt {
  x: number;
  y: number;
}

/** Flat ring coordinates [x0,y0,...], implicitly closed. */
export type Ring = Float64Array;

export interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// ---------- document model (import side) ----------

export interface LayerInfo {
  /** CutSetting index (0..29 for C00..C29, but tolerate any int). */
  index: number;
  name: string;
  /** "#RRGGBB" */
  color: string;
  /** Number of importable vector shapes on this layer. */
  shapeCount: number;
}

/** One flattened polyline/polygon extracted from the source document. */
export interface PolyGeom {
  layer: number;
  /** Flat [x,y,...] in project mm space (transforms already applied). */
  points: Float64Array;
  closed: boolean;
  /** Source shape type, for diagnostics ("Path" | "Rect" | ...). */
  source: string;
}

export interface ImportWarning {
  kind: "skip" | "warn";
  message: string;
  /** How many times this exact message occurred (aggregated). */
  count: number;
}

/** Parsed source document (LightBurn project or SVG), ready for extraction. */
export interface VectorDocument {
  kind: "lbrn" | "svg";
  fileName: string;
  layers: LayerInfo[];
  /**
   * For lbrn: the original XML string (plain, already unzipped) so exports can
   * write a faithful project copy. For svg: the original SVG text.
   */
  sourceText: string;
  /** Extract flattened geometry for the given layers. curveSteps clamps 3..96. */
  extractGeometry(selectedLayers: ReadonlySet<number>, curveSteps: number): {
    geoms: PolyGeom[];
    warnings: ImportWarning[];
  };
}

// ---------- pixelation settings ----------

export type DotShape = "circle" | "square" | "diamond" | "hexagon" | "octagon" | "triangle" | "star" | "heart" | "cross";
export type PixelMode = "auto" | "fill" | "trace";
export type GridOrigin = "project" | "bbox";
/** Edge treatment: "none" = grid dots only; "clip" = exact even-odd boolean clip. */
export type EdgeMode = "none" | "clip";

export interface PixelSettings {
  pixelSize: number; // mm, > 0
  spacingX: number; // mm, > 0
  spacingY: number; // mm, > 0
  shape: DotShape;
  mode: PixelMode;
  origin: GridOrigin;
  offsetX: number; // mm
  offsetY: number; // mm
  stagger: boolean;
  /**
   * Regular-hexagon packing: force the down-pitch to spacingX·√3/2 and stagger
   * odd rows by half a column, so every dot is equidistant from its six
   * neighbours. Overrides spacingY + stagger while on.
   */
  hexPacking: boolean;
  /**
   * Vertical stretch of each dot's shape (height ÷ width). 1 = as authored;
   * >1 taller, <1 wider. Lets a diamond become a tall rhombus, etc.
   */
  aspect: number;
  /**
   * Mirror the dot shape vertically on staggered (odd) rows. Meant for hearts,
   * so alternating rows point down/up and nest together.
   */
  flipOffset: boolean;
  curveSteps: number; // bezier flattening samples, clamp 3..96, default 16
  /** Edge treatment for partial pixels along shape outlines. */
  edgeMode: EdgeMode;
  /** Hard safety cap on emitted pixels (default 200_000). */
  maxPixels: number;
}

export const DEFAULT_PIXEL_SETTINGS: PixelSettings = {
  pixelSize: 0.12,
  spacingX: 0.18,
  spacingY: 0.18,
  shape: "circle",
  mode: "auto",
  origin: "bbox",
  offsetX: 0,
  offsetY: 0,
  stagger: false,
  hexPacking: false,
  aspect: 1,
  flipOffset: false,
  curveSteps: 16,
  edgeMode: "clip",
  maxPixels: 200_000,
};

// ---------- pixel output ----------

/**
 * One emitted pixel. Full dots have rings === undefined and are drawn/exported
 * as the parametric shape at (x, y) scaled by sizeFactor·pixelSize. Clipped
 * pixels carry explicit ring geometry (even-odd, absolute mm coords).
 */
export interface Pixel {
  x: number;
  y: number;
  layer: number;
  /** Scale factor applied to pixelSize for the parametric shape (1 = full dot). */
  sizeFactor: number;
  /** Vertically mirror the parametric shape (staggered-row hearts). */
  flipY?: boolean;
  /** Present only for edge-clipped partial pixels. */
  rings?: Ring[];
}

export interface PixelResult {
  pixels: Pixel[];
  truncated: boolean;
  checkedPoints: number;
  clippedCount: number;
  /** Per-layer counts for the UI. */
  countsByLayer: Record<number, number>;
}

// ---------- photo engine ----------

export type PaletteMode = "adaptive" | "lightburn";
export type LayerSortMode = "dark-to-light" | "light-to-dark" | "most-used" | "hue";

export interface PhotoSettings {
  maxLayers: number; // 1..30
  outputWidthMm: number;
  pixelSize: number; // mm
  spacingX: number; // mm
  spacingY: number; // mm
  shape: DotShape;
  paletteMode: PaletteMode;
  sortMode: LayerSortMode;
  dither: boolean;
  ignoreTransparent: boolean;
  ignoreNearWhite: boolean;
  /** First C-layer index for "lightburn" palette mode (0..29). */
  startLayer: number;
  maxPixels: number;
  /** Merge adjacent same-layer full squares into row-run rects (square only). */
  optimizeSquareRuns: boolean;
}

export const DEFAULT_PHOTO_SETTINGS: PhotoSettings = {
  maxLayers: 16,
  outputWidthMm: 60,
  pixelSize: 0.3,
  spacingX: 0.3,
  spacingY: 0.3,
  shape: "square",
  paletteMode: "adaptive",
  sortMode: "dark-to-light",
  dither: true,
  ignoreTransparent: true,
  ignoreNearWhite: false,
  startLayer: 0,
  maxPixels: 120_000,
  optimizeSquareRuns: false,
};

/** Verified LightBurn C00..C29 default layer colors. */
export const LIGHTBURN_PALETTE: readonly string[] = [
  "#000000", "#0000FF", "#FF0000", "#00E000", "#D0D000", "#FF8000",
  "#00E0E0", "#FF00FF", "#B4B4B4", "#0000A0", "#A00000", "#00A000",
  "#A0A000", "#C08000", "#00A0FF", "#A000A0", "#808080", "#7D87B9",
  "#BB7784", "#4A6FE3", "#D33F6A", "#8CD78C", "#F0B98D", "#F6C4E1",
  "#FA9ED4", "#500A78", "#B45A00", "#004754", "#86FA88", "#FFDB66",
];

export interface PhotoPaletteEntry {
  /** C-layer index this group exports as. */
  layerIndex: number;
  /** Color written to the SVG (layer color in lightburn mode). */
  svgColor: string;
  /** Original quantized image color this layer represents. */
  targetColor: string;
  pixelCount: number;
  percent: number;
  /** Euclidean RGB distance between svgColor and targetColor. */
  paletteError: number;
}

/**
 * Photo result grid: cell value = palette entry ordinal + 1, 0 = skipped/empty.
 * Geometry derives from grid position: center x = originX + col·spacingX, etc.
 */
export interface PhotoResult {
  cols: number;
  rows: number;
  originX: number;
  originY: number;
  spacingX: number;
  spacingY: number;
  pixelSize: number;
  shape: DotShape;
  grid: Uint8Array; // length cols·rows, row-major
  palette: PhotoPaletteEntry[];
  paletteMode: PaletteMode;
  sourceName: string;
  sourceWidth: number;
  sourceHeight: number;
  widthMm: number;
  heightMm: number;
  visibleCount: number;
  notes: string[];
}

/** Raw image handed to the photo engine (decoded on the main thread). */
export interface RawImage {
  width: number;
  height: number;
  /** RGBA8, length = width·height·4. */
  data: Uint8ClampedArray;
  name: string;
}

/** Photo-fill options for colorizing vector pixel output from an image. */
export interface PhotoFillSettings {
  enabled: boolean;
  fit: "cover" | "contain" | "stretch";
  maxLayers: number;
  paletteMode: PaletteMode;
  sortMode: LayerSortMode;
  dither: boolean;
  startLayer: number;
}

export const DEFAULT_PHOTO_FILL: PhotoFillSettings = {
  enabled: false,
  fit: "cover",
  maxLayers: 12,
  paletteMode: "adaptive",
  sortMode: "dark-to-light",
  dither: true,
  startLayer: 0,
};

/** Result of photo-fill: same pixels, re-assigned to photo palette layers. */
export interface PhotoFillResult {
  pixels: Pixel[];
  palette: PhotoPaletteEntry[];
  countsByLayer: Record<number, number>;
}

// ---------- worker protocol ----------

export interface ProgressMsg {
  type: "progress";
  jobId: number;
  stage: string;
  /** 0..1 when computable, -1 for indeterminate. */
  fraction: number;
  pixelCount: number;
}

export type PixelateRequest = {
  type: "pixelate";
  jobId: number;
  /** Serialized geometry (PolyGeom without methods). */
  geoms: { layer: number; points: Float64Array; closed: boolean; source: string }[];
  selectedLayers: number[];
  settings: PixelSettings;
  /** Soft cap for live preview; when reached, return truncated result. */
  softLimit: boolean;
  maxPixelsOverride?: number;
  photoFill?: { settings: PhotoFillSettings; image: RawImage };
};

export type PixelateResponse =
  | ProgressMsg
  | { type: "done"; jobId: number; result: PixelResult; photoFill?: PhotoFillResult }
  | { type: "error"; jobId: number; message: string }
  | { type: "cancelled"; jobId: number };

export type PhotoRequest = {
  type: "photo";
  jobId: number;
  image: RawImage;
  settings: PhotoSettings;
};

export type PhotoResponse =
  | ProgressMsg
  | { type: "done"; jobId: number; result: PhotoResult }
  | { type: "error"; jobId: number; message: string }
  | { type: "cancelled"; jobId: number };

/** Sent to either worker: abandon the job with this id (best-effort). */
export type CancelRequest = { type: "cancel"; jobId: number };

// ---------- laser cut parameters (MOPA color marking) ----------

/**
 * Per-layer laser parameters. When present on export, they are embedded into
 * the .lbrn2 CutSetting elements so the file runs without manual setup, and
 * printed in the palette report. All values are operator-editable seeds —
 * MOPA colors are machine/material/focus specific and need test-grid calibration.
 */
export interface CutParams {
  /** 0..100 (%) */
  power: number;
  /** mm/s */
  speed: number;
  /** kHz, 1..4000 */
  frequencyKhz: number;
  /** ns — standard MOPA steps (2..500) */
  qPulseNs: number;
  /** hatch line interval, mm — color fills go down to 0.001 */
  intervalMm: number;
  passes: number;
  /** Scan/hatch angle in degrees (0..360). */
  hatchAngleDeg: number;
  /** Cross-hatch: run a second pass at 90° to the scan angle. */
  crossHatch: boolean;
  /** Bi-directional scanning (fill on both sweep directions). */
  bidir: boolean;
  /** Fill all shapes at once (true) vs each shape individually (false). */
  fillAllAtOnce: boolean;
}

export interface MaterialPreset {
  name: string;
  /** e.g. "100W MOPA", "60W MOPA" — neutral, no vendor names. */
  machine: string;
  material: string;
  /** Map of "#RRGGBB" target color -> params. */
  colors: { color: string; label: string; params: CutParams }[];
}

/** Standard MOPA Q-pulse width steps in ns. */
export const Q_PULSE_STEPS: readonly number[] = [
  2, 4, 6, 8, 10, 13, 15, 20, 25, 30, 40, 45, 50, 60, 80, 100, 150, 200, 250, 350, 500,
];

// ---------- test grid generator ----------

export type SweepParam = "power" | "speed" | "frequencyKhz" | "qPulseNs" | "intervalMm";

export interface TestGridSettings {
  rowParam: SweepParam;
  rowValues: number[]; // <= 8
  colParam: SweepParam;
  colValues: number[]; // <= 8
  /** Base parameters used for the non-swept fields. */
  base: CutParams;
  swatchSizeMm: number; // default 8
  gapMm: number; // default 2, >= 1
  labelCells: boolean;
}

export const DEFAULT_TEST_GRID: TestGridSettings = {
  rowParam: "qPulseNs",
  rowValues: [2, 6, 13, 25, 60, 200],
  colParam: "frequencyKhz",
  colValues: [200, 300, 500, 700, 1000, 1500],
  base: { power: 20, speed: 800, frequencyKhz: 500, qPulseNs: 6, intervalMm: 0.002, passes: 1, hatchAngleDeg: 0, crossHatch: false, bidir: true, fillAllAtOnce: false },
  swatchSizeMm: 8,
  gapMm: 2,
  labelCells: true,
};

// ---------- export inputs ----------

export interface VectorExportContext {
  pixels: Pixel[];
  settings: PixelSettings;
  /** layer index -> info, for colors/names. */
  layers: Record<number, LayerInfo>;
  /** Optional photo-fill palette: when present, layer colors/names come from it. */
  photoPalette?: PhotoPaletteEntry[];
  /** Optional per-layer laser params to embed in .lbrn2 CutSettings + report. */
  cutParams?: Record<number, CutParams>;
}

// ---------- units + i18n contract (T3-UI) ----------

/** Global measurement system applied to every dimension field, ruler + output. */
export type UnitSystem = "mm" | "inch";

export const MM_PER_INCH = 25.4;

/**
 * Shared number-format / parse helper signatures. Implementations live in
 * core/geometry (svg %.6f writer) and ui/state (locale-tolerant field I/O).
 * `parseLocaleNumber` accepts comma-or-period decimals ("0,25" -> 0.25).
 * `formatDim` renders a mm value in the active UnitSystem for display.
 */
export type NumFormatter = (n: number) => string;
export type LocaleNumberParser = (raw: string) => number | null;
export type DimFormatter = (mm: number, unit: UnitSystem, decimals?: number) => string;

// ---------- MOPA per-layer legend record (T1 + T6 + T8) ----------

/**
 * Complete, self-contained per-color MOPA record that travels alongside every
 * export (CSV legend, SVG metadata, .lbrn2 CutSetting). This is the minimum
 * reproducible record so a burn can be recreated on any machine. Units are
 * fixed and carried explicitly in an accompanying units line on export.
 */
export interface MopaLayerRecord {
  /** C-layer index (export layer id). */
  index: number;
  /** 1-based run order in the job. */
  runOrder: number;
  name: string;
  /** Target color as "#RRGGBB". */
  targetColorHex: string;
  power: number; // %
  speed: number; // mm/s
  frequencyKhz: number; // kHz
  pulseWidthNs: number; // ns (MOPA Q-pulse width)
  hatchSpacingMm: number; // mm (line interval)
  hatchAngleDeg: number; // degrees
  hatchType: "line" | "cross" | "fill";
  passes: number;
  focusOffsetMm: number; // mm relative to nominal focus
}

// ---------- calibration swatch library (T1 + T3) ----------

/**
 * One measured calibration point: an operator burned a swatch at these
 * parameters and measured the resulting color. Photo quantization targets the
 * set of measured colors so output uses only achievable colors.
 */
export interface SwatchEntry {
  id: string;
  /** Measured resulting color as "#RRGGBB". */
  measuredColor: string;
  /** Optional operator label ("warm gold", "steel blue"). */
  label: string;
  params: CutParams;
  /** Focus offset in mm for this swatch (0 = nominal). */
  focusOffsetMm: number;
}

/**
 * Calibration library scoped to a machine + material + surface finish. Photo
 * mode quantizes to the measured colors of the active library.
 */
export interface SwatchLibrary {
  id: string;
  name: string;
  /** Neutral machine descriptor, e.g. "60W MOPA" — no vendor names. */
  machine: string;
  material: string;
  /** Surface finish, e.g. "brushed", "polished", "bead-blasted". */
  finish: string;
  swatches: SwatchEntry[];
}

export const APP_VERSION = "3.2.2.1";
export const APP_NAME = "MOPA Pixelator Web";
